import { memo } from 'react'
import { styled, Box, Typography, Chip, Alert, Tabs, Tab } from '@mui/material'

// Main container styles
export const StyledSection = styled('section')(({ theme }) => ({
  backgroundColor: '#ffffff',
  padding: '6px',
  minHeight: '100vh',
  [theme.breakpoints.down('md')]: {
    padding: '16px',
  },
}))

export const StyledContainer = styled('div')(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}))

// Header section styles
export const StyledHeaderBox = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
}))

export const StyledMainTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  marginBottom: '16px',
}))

export const StyledHeaderDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(0, 0, 0, 0.6)',
}))

// Alert styling
export const StyledAlert = styled(Alert)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '8px',
}))

// Component title styling
export const StyledComponentTitle = styled(Typography)(({ theme }) => ({
  color: '#333',
  fontWeight: 700,
  marginBottom: '16px',
  marginTop: '16px',
}))

// SubSection styles
export const StyledSubSectionBox = styled(Box)(({ theme }) => ({
  marginBottom: '20px',
}))

export const StyledSubSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '8px',
}))

export const StyledSubSectionDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(0, 0, 0, 0.6)',
  marginBottom: '12px',
}))

export const StyledSizeLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  fontSize: '1rem',
  marginTop: '24px',
  marginBottom: '8px',
}))

// Button demo container styles
export const StyledDemoGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '32px',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
}))

export const StyledDemoComponent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '8px',
  minWidth: '120px',
}))

export const StyledDemoChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.7rem',
  height: '20px',
  backgroundColor: '#e3f2fd',
  color: theme.palette.primary.main,
  fontWeight: 500,
  borderColor: theme.palette.primary.main,
  '& .MuiChip-label': {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
}))

// Custom styling for any potential code blocks
export const StyledCodeBlock = styled('pre')(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: '12px',
  borderRadius: '4px',
  fontSize: '12px',
  lineHeight: 1.4,
  overflowX: 'auto',
}))

// Paper component override if needed
export const StyledPaper = styled('div')(({ theme }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  padding: '16px',
}))

export const SubSection = ({ title, description, children }) => (
  <StyledSubSectionBox>
    <StyledSubSectionTitle variant='h5' gutterBottom>
      {title}
    </StyledSubSectionTitle>
    {description && (
      <StyledSubSectionDescription variant='body2'>
        {description}
      </StyledSubSectionDescription>
    )}
    {children}
  </StyledSubSectionBox>
)

export const DemoComponent = memo(({ label, component, value }) => (
  <StyledDemoComponent>
    {component}
    {value && (
      <Typography variant='caption' sx={{ mt: 0.5, wordBreak: 'break-all' }}>
        {String(value)}
      </Typography>
    )}
    <StyledDemoChip label={label} size='small' variant='filled' />
  </StyledDemoComponent>
))
